import { useState } from 'react';
import {
  ArrowRight,
  Check,
  LockKeyhole,
  Menu,
  MessageCircleMore,
  Sparkles,
  UserRound,
  X,
} from 'lucide-react';
import './LandingPage.css';

const DEMO_STATES = {
  boss: {
    label: 'Your boss',
    message: 'Can you send me the numbers before lunch?',
    result: 'Could you share the figures before noon? I want to review them ahead of our call.',
    note: 'Clear, concise, not defensive.',
    color: 'yellow',
  },
  friend: {
    label: 'Your friend',
    message: 'yeah will do, sending now',
    result: 'On it. I’ll send it over now.',
    note: 'Casual, warm, keeps the momentum.',
    color: 'pink',
  },
  client: {
    label: 'Your client',
    message: 'We should probably revisit the timeline',
    result: 'I think the timeline needs a quick reset. I’ll share two options so we can keep the project moving.',
    note: 'Helpful, confident, makes the next step obvious.',
    color: 'lime',
  },
};

function Logo() {
  return (
    <a className="landing-logo" href="#top" aria-label="Vibe Translator home">
      <span className="landing-logo-mark">V</span>
      <span>Vibe Translator</span>
    </a>
  );
}

function ProductDemo() {
  const [activeDemo, setActiveDemo] = useState('boss');
  const demo = DEMO_STATES[activeDemo];

  return (
    <div className="demo-window" aria-label="Interactive Vibe Translator preview">
      <div className="demo-window-bar">
        <div className="window-dots" aria-hidden="true"><span /><span /><span /></div>
        <span className="window-title">Vibe Translator / preview</span>
        <span className="window-status"><span className="status-dot" /> ready</span>
      </div>

      <div className="demo-body">
        <div className="demo-side">
          <div className="demo-side-label">Rewrite for</div>
          <div className="demo-people">
            {Object.entries(DEMO_STATES).map(([id, item]) => (
              <button
                key={id}
                className={`demo-person ${activeDemo === id ? 'is-active' : ''}`}
                onClick={() => setActiveDemo(id)}
                type="button"
              >
                <span className={`demo-avatar ${item.color}`}><UserRound size={16} /></span>
                <span>{item.label}</span>
                {activeDemo === id && <ArrowRight size={14} />}
              </button>
            ))}
          </div>
          <div className="demo-side-footer">
            <LockKeyhole size={14} />
            <span>Profiles stay on your device</span>
          </div>
        </div>

        <div className="demo-main">
          <div className="demo-context-row">
            <span className="demo-kicker">Message context</span>
            <span className="demo-recipient">{demo.label}</span>
          </div>
          <p className="demo-message">“{demo.message}”</p>
          <div className="demo-divider" />
          <div className="demo-output-label"><Sparkles size={14} /> Vibe Translator suggests</div>
          <p className="demo-result">{demo.result}</p>
          <div className="demo-result-note"><Check size={15} /> {demo.note}</div>
          <button className="demo-insert" type="button">Preview in keyboard <ArrowRight size={15} /></button>
        </div>
      </div>

      <div className="demo-tabs" role="tablist" aria-label="Demo mode">
        <span className="is-selected">Rewrite</span>
        <span>Decode</span>
        <span>Play</span>
      </div>
    </div>
  );
}

function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const closeMenu = () => setMobileMenuOpen(false);

  return (
    <main className="landing-page" id="top">
      <nav className="landing-nav" aria-label="Primary navigation">
        <div className="landing-nav-inner">
          <Logo />
          <div className={`landing-nav-links ${mobileMenuOpen ? 'is-open' : ''}`}>
            <a href="#how-it-works" onClick={closeMenu}>How it works</a>
            <a href="#people" onClick={closeMenu}>People profiles</a>
            <a href="#privacy" onClick={closeMenu}>Privacy</a>
            <a className="nav-cta" href="/v3?preview=1" onClick={closeMenu}>Try Vibe <ArrowRight size={15} /></a>
          </div>
          <button
            className="mobile-menu-toggle"
            type="button"
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileMenuOpen}
            onClick={() => setMobileMenuOpen((open) => !open)}
          >
            {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </nav>

      <section className="landing-hero" aria-labelledby="hero-title">
        <div className="hero-copy">
          <div className="hero-eyebrow"><span className="eyebrow-dot" /> Intent translation for real life</div>
          <h1 id="hero-title">Say what you mean.<br /><em>Hear what they meant.</em></h1>
          <p className="hero-subtitle">Vibe Translator helps you understand a message in context and send the version that will actually land.</p>
          <div className="hero-actions">
            <a className="button button-primary" href="/v3?preview=1">Try Vibe Translator <ArrowRight size={17} /></a>
            <a className="text-link" href="#how-it-works">See how it works <ArrowRight size={15} /></a>
          </div>
          <div className="hero-proof"><LockKeyhole size={14} /> User-authored profiles. Device-local by default.</div>
        </div>
        <div className="hero-visual">
          <img className="landing-graphic-sticker" src="/logo512.png" alt="" aria-hidden="true" />
          <div className="hero-note note-one"><MessageCircleMore size={15} /> Less guessing</div>
          <ProductDemo />
          <div className="hero-note note-two"><span className="note-line" /> More clarity</div>
        </div>
      </section>

      <section className="signal-strip" aria-label="Product principles">
        <div><span className="signal-number">01</span><strong>Context over tone</strong><span>Meaning changes with the person.</span></div>
        <div><span className="signal-number">02</span><strong>Profiles over prompts</strong><span>Save what you learn once.</span></div>
        <div><span className="signal-number">03</span><strong>Preview over autopilot</strong><span>You decide what gets sent.</span></div>
      </section>

      <section className="story-section" id="how-it-works" aria-labelledby="story-title">
        <div className="section-intro">
          <span className="section-index">A better layer between thought and send</span>
          <h2 id="story-title">The same sentence can land three different ways.</h2>
          <p>Vibe Translator gives the message a reader before it gives you a rewrite.</p>
        </div>
        <div className="story-grid">
          <article className="story-card story-card-dark">
            <div className="story-card-top"><span>01 / Read the room</span><MessageCircleMore size={19} /></div>
            <h3>Understand the likely meaning.</h3>
            <p>Decode the subtext, the ask, and the parts that could be misread.</p>
            <div className="chat-bubble bubble-dark">“We should talk sometime.”</div>
            <div className="chat-bubble bubble-light">Could mean: a real conversation is overdue.</div>
          </article>
          <article className="story-card story-card-yellow">
            <div className="story-card-top"><span>02 / Choose the reader</span><UserRound size={19} /></div>
            <h3>Pick the person, not a generic tone.</h3>
            <p>Boss, client, friend, partner — different people need different context.</p>
            <div className="profile-stack">
              <span><span className="mini-avatar mini-yellow" /> Boss <b>direct · concise</b></span>
              <span><span className="mini-avatar mini-pink" /> Friend <b>warm · casual</b></span>
              <span><span className="mini-avatar mini-lime" /> Client <b>clear · helpful</b></span>
            </div>
          </article>
          <article className="story-card story-card-pink">
            <div className="story-card-top"><span>03 / Send with intent</span><Sparkles size={19} /></div>
            <h3>Preview it before it leaves.</h3>
            <p>Vibe keeps you in control. Nothing gets sent until it sounds right to you.</p>
            <div className="send-preview"><span>Preview</span><strong>Could you share the numbers by noon?</strong><button type="button">Insert <ArrowRight size={14} /></button></div>
          </article>
        </div>
      </section>

      <section className="profiles-section" id="people" aria-labelledby="profiles-title">
        <div className="profiles-visual" aria-hidden="true">
          <div className="profile-orbit orbit-a" />
          <div className="profile-orbit orbit-b" />
          <div className="profile-card-main">
            <div className="profile-card-header"><span className="profile-card-label">Person profile</span><span className="profile-card-dot" /></div>
            <div className="profile-card-name"><span className="large-avatar">A</span><div><strong>Alex</strong><span>Friend · 3 years</span></div></div>
            <div className="profile-trait"><span>Directness</span><div><i style={{ width: '74%' }} /></div><b>4 / 5</b></div>
            <div className="profile-trait"><span>Warmth</span><div><i style={{ width: '88%' }} /></div><b>5 / 5</b></div>
            <div className="profile-trait"><span>Detail</span><div><i style={{ width: '42%' }} /></div><b>2 / 5</b></div>
            <div className="profile-quote">“Keep it warm. Get to the point.”</div>
          </div>
          <div className="floating-tag tag-top"><Check size={14} /> User-authored</div>
          <div className="floating-tag tag-bottom"><LockKeyhole size={14} /> Yours, not ours</div>
        </div>
        <div className="profiles-copy">
          <span className="section-index">The compounding advantage</span>
          <h2>People are the context.</h2>
          <p>Generic AI can make a sentence sound nicer. Vibe Translator learns the difference between how your boss, your best friend, and your client will hear the same sentence.</p>
          <ul className="check-list">
            <li><Check size={16} /> Add the context only you know</li>
            <li><Check size={16} /> Adjust as the relationship changes</li>
            <li><Check size={16} /> Use the same profile anywhere you write</li>
          </ul>
          <a className="text-link" href="/v3?preview=1">Meet People profiles <ArrowRight size={15} /></a>
        </div>
      </section>

      <section className="privacy-section" id="privacy" aria-labelledby="privacy-title">
        <div className="privacy-copy">
          <span className="section-index">Built for the sensitive bits</span>
          <h2>Private by default.<br />Useful by design.</h2>
          <p>Your profiles are user-authored context, not a hidden dossier. You choose what to add, what to use, and what to send.</p>
          <div className="privacy-points">
            <div><LockKeyhole size={18} /><span><strong>Device-local profiles</strong><br />Your people context stays with you by default.</span></div>
            <div><Check size={18} /><span><strong>Preview before insert</strong><br />Vibe suggests. You stay in the loop.</span></div>
          </div>
        </div>
        <div className="privacy-statement"><span className="statement-mark">“</span><p>AI should help you communicate with more intention — not make decisions about your relationships for you.</p><span className="statement-caption">Vibe Translator / product principle</span></div>
      </section>

      <section className="final-cta" aria-labelledby="cta-title">
        <div className="cta-grid-mark" aria-hidden="true"><span /><span /><span /><span /></div>
        <span className="section-index">Ready when you are</span>
        <h2 id="cta-title">Make the next message<br /><em>the right one.</em></h2>
          <a className="button button-dark final-cta-button" href="/v3?preview=1">
            <span>TRY VIBE — IT'S FREE</span>
            <ArrowRight size={19} aria-hidden="true" />
          </a>
        <p>No autopilot. No mind reading. Just better context.</p>
      </section>

      <footer className="landing-footer">
        <Logo />
        <span>Intent translation for real life.</span>
        <div><a href="#privacy">Privacy</a><a href="/app">Open app</a><span>© 2026 Vibe Translator</span></div>
      </footer>
    </main>
  );
}

export default LandingPage;
